import { useMemo, type JSX, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { LazyMermaidBlock } from '@/features/diagrams/LazyMermaidBlock.js';
import type { MermaidExportTarget } from '@/features/diagrams/mermaid-block-context.js';

/** GFM gives the tables, task lists and strikethrough a real `.md` file relies on. */
const remarkPlugins = [remarkGfm];

/**
 * Renders a markdown artifact. Deliberately NOT the chat's `MessageBody`: that one carries chat
 * semantics a file preview must not inherit (streaming caret, email-preview cards, deck-download
 * links, in-app router links).
 *
 * The file's bytes are untrusted — an agent wrote them, or another workspace member uploaded them —
 * so two things are load-bearing here:
 *
 * - **No raw HTML.** `react-markdown` ignores embedded HTML unless `rehype-raw` is added; it is not.
 *   Unsafe URL schemes (`javascript:` and friends) are stripped by its own URL filter.
 * - **Images render as links, never as `<img>`.** A remote `![](https://…)` would make the app origin
 *   fetch a third party's URL for whoever opens the file — a viewer-tracking side channel in a shared
 *   workspace. And a relative `![](chart.png)` cannot resolve at all (the artifact's bytes have no
 *   base URL), so it would only ever render as a broken image. A labelled link is honest in both cases.
 *
 * Styling comes from the shared `.markdown-content` rules in `globals.css`, so a rendered file matches
 * the rest of the app's markdown without a second style vocabulary.
 *
 * A ```mermaid block is drawn in place with the editor's strict renderer. With `mermaidTarget` each
 * block can be exported to Diagrams, named after the file, with `#n` when the file holds several.
 */
export function ArtifactMarkdown({
  text,
  mermaidTarget = null,
}: {
  text: string;
  mermaidTarget?: MermaidExportTarget | null;
}): JSX.Element {
  /** Primitives, so a caller passing a new object each render does not remount every block. */
  const workspaceId = mermaidTarget?.workspaceId;
  const title = mermaidTarget?.title;
  const components = useMemo(
    () => ({
      ...markdownComponents,
      ...mermaidComponents(
        text,
        workspaceId === undefined ? null : { workspaceId, title: title ?? '' },
      ),
    }),
    [text, workspaceId, title],
  );
  return (
    <div className="markdown-content break-words text-sm">
      <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

const markdownComponents = {
  /** Every link leaves the app: a file's links are external by nature, and `noreferrer` keeps the
   *  app's URL out of the target's logs. */
  a: ({ href, children }: { href?: string; children?: ReactNode }) => (
    <a href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  ),
  /** See the component's note: an image becomes a link, so nothing is fetched on the viewer's behalf. */
  img: ({ src, alt }: { src?: string | Blob; alt?: string }) => {
    const label = alt?.trim();
    if (typeof src !== 'string' || src.length === 0) {
      return <span className="text-muted-foreground">{label ?? 'image'}</span>;
    }
    return (
      <a href={src} target="_blank" rel="noreferrer noopener">
        {label && label.length > 0 ? label : src}
      </a>
    );
  },
};

/** Where each ```mermaid fence starts in the file, so a block knows its number. */
function mermaidFenceOffsets(text: string): number[] {
  return [...text.matchAll(/^ {0,3}(`{3,}|~{3,})\s*mermaid\b/gm)].map(
    (match) => match.index,
  );
}

type MarkdownNode = import('hast').Element;

/** The class of the first child element, which is where a fenced block puts its language. */
function firstChildClass(node: MarkdownNode | undefined): unknown {
  const child = node?.children?.[0];
  return child?.type === 'element' ? child.properties?.className : undefined;
}

function isMermaidClass(className: unknown): boolean {
  const classes = Array.isArray(className) ? className : [className];
  return classes.some((name) => String(name) === 'language-mermaid');
}

/**
 * ```mermaid blocks drawn as diagrams. The `pre` around one is dropped, since the block draws its
 * own frame; every other code block keeps the default rendering.
 */
function mermaidComponents(text: string, target: MermaidExportTarget | null) {
  const offsets = mermaidFenceOffsets(text);
  const titleFor = (offset: number | undefined): string => {
    if (!target) return '';
    if (offsets.length < 2 || offset === undefined) return target.title;
    const number = offsets.filter((start) => start <= offset).length;
    return `${target.title} #${Math.max(number, 1)}`;
  };
  return {
    pre: ({ node, children }: { node?: MarkdownNode; children?: ReactNode }) =>
      isMermaidClass(firstChildClass(node)) ? (
        <>{children}</>
      ) : (
        <pre>{children}</pre>
      ),
    code: ({
      node,
      className,
      children,
    }: {
      node?: MarkdownNode;
      className?: string;
      children?: ReactNode;
    }) =>
      className === 'language-mermaid' ? (
        <LazyMermaidBlock
          source={String(children).replace(/\n$/, '')}
          target={
            target
              ? { ...target, title: titleFor(node?.position?.start?.offset) }
              : null
          }
        />
      ) : (
        <code className={className}>{children}</code>
      ),
  };
}
